import type { HealthAction } from "@safers/api";

import type { HeatLevel } from "@safers/ui";

export {
  HEAT_LEVEL_META,
  HEAT_STEPS,
  heatLevelFromSeverity,
  heatLevelOf,
  heatLevelOfPoint,
  type HeatLevel,
  type HeatLevelMeta,
} from "@safers/ui";

/** 가스 4종 — 대시보드 타일과 이벤트 상세 모달이 같은 순서·라벨·단위로 그린다. */
export const GAS_FIELDS = [
  { key: "o2", label: "O₂", name: "산소", unit: "%" },
  { key: "h2s", label: "H₂S", name: "황화수소", unit: "ppm" },
  { key: "co", label: "CO", name: "일산화탄소", unit: "ppm" },
  { key: "ch4", label: "CH₄", name: "메탄", unit: "%LEL" },
] as const;

export type GasKey = (typeof GAS_FIELDS)[number]["key"];

/**
 * 스마트밴드 4종 — 현장 대시보드 타일 순서·라벨·단위.
 */
export const BAND_FIELDS = [
  { key: "heartbeat", label: "심박수", unit: "bpm" },
  { key: "temperature", label: "체온", unit: "℃" },
  { key: "high_heartbeat", label: "최고 심박수", unit: "bpm" },
  { key: "battery", label: "배터리", unit: "%" },
] as const;

export const GAS_BOUNDS: Partial<Record<GasKey, readonly number[]>> = {};

export interface HealthSensor {
  id: number;
  name: string;
}

export type ActionKind = "REST" | "OUTDOOR_STOP" | "WORK_END" | "CUSTOM";

export const ACTION_KINDS: { value: ActionKind; label: string }[] = [
  { value: "REST", label: "휴식" },
  { value: "OUTDOOR_STOP", label: "옥외작업 중단" },
  { value: "WORK_END", label: "작업종료" },
  { value: "CUSTOM", label: "직접입력" },
];

export const REST_MINUTES = [10, 15, 20, 25, 30];
export const DEFAULT_REST_MINUTES = 20;

export function composeActionType(kind: ActionKind, restMinutes: number, custom: string): string {
  switch (kind) {
    case "REST":
      return `휴식-${restMinutes}분`;
    case "OUTDOOR_STOP":
      return "옥외작업 중단";
    case "WORK_END":
      return "작업종료";
    case "CUSTOM":
      return custom.trim();
  }
}

const REST_TEXT = /^휴식[-\s](\d+)분$/;

export function parseActionType(actionType: string): {
  kind: ActionKind;
  restMinutes: number;
  custom: string;
} {
  const rest = REST_TEXT.exec(actionType);
  if (rest) return { kind: "REST", restMinutes: Number(rest[1]), custom: "" };
  if (actionType === "옥외작업 중단")
    return { kind: "OUTDOOR_STOP", restMinutes: DEFAULT_REST_MINUTES, custom: "" };
  if (actionType === "작업종료")
    return { kind: "WORK_END", restMinutes: DEFAULT_REST_MINUTES, custom: "" };
  return { kind: "CUSTOM", restMinutes: DEFAULT_REST_MINUTES, custom: actionType };
}

export function actionTypeParts(actionType: string): { label: string; detail: string } {
  const { kind, custom } = parseActionType(actionType);
  if (kind === "REST") return { label: actionType, detail: "" };
  const label = ACTION_KINDS.find((k) => k.value === kind)?.label ?? actionType;
  if (kind === "CUSTOM") return { label: "기타", detail: custom };
  return { label, detail: "" };
}

export interface HealthActionInput {
  actionType: string;
  worker: string;
  deviceId?: number | null;
  temperature: number;
  humidity: number;
  heatIndex: number;
  measuredTime: string;
  author?: string;
  actionAt: string;
  newFiles: File[];
  keepFileIds: (number | File)[];
}

export const HEALTH_PHOTO_MAX = 5;

export type ReportRow = { label: string } & Record<string, number | string>;

export interface DailyReport {
  siteName: string;
  date: string;
  sensorCount: number;
  seriesNames: string[];
  hourly: ReportRow[];
  actions: HealthAction[];
}

export interface MonthlyDailyPoint {
  date: string;
  maxHeatIndex: number;
  avgHeatIndex: number;
  actionCount: number;
}

/**
 * 센서 하나의 등급별 알림 수.
 *
 * **월간 레포트에는 기상청이 들어가지 않는다** — 현장에 설치된 센서만 센다(백엔드 의도).
 * 스펙 설명에는 "맨 앞은 항상 기상청" 이라고 적혀 있으나 실제 응답에는 없다. 순서를
 * 가정하지 말고 받은 대로 쓴다.
 */
export interface SensorAlarmRow {
  name: string;
  count: number;
  /** 등급별 건수. 서버 6단을 보건 5단으로 접어 합산한 결과 */
  byLevel: { level: HeatLevel; label: string; count: number }[];
}

export interface MonthlyReport {
  siteName: string;
  yearMonth: string;
  daily: MonthlyDailyPoint[];
  levelCounts: { level: HeatLevel; label: string; count: number }[];
  totalActions: number;
  /** 월 전체 온열 알림 수. 화면이 '알림 없음' 을 한 번만 말하도록 미리 센다 */
  totalAlarms: number;
  sensorAlarms: SensorAlarmRow[];
}
